// @ts-check

/** Page size for admin list AJAX requests. */
export const ADMIN_PAGE_LIMIT = 30;

/**
 * @typedef {Object} AdminPageMeta
 * @property {number} total
 * @property {number} page
 * @property {number} pageSize
 * @property {number} pageCount
 */

/**
 * Builds clamped pagination metadata. `pageCount` is always at least 1 so a
 * pager can render even when the list is empty.
 *
 * @param {number} total
 * @param {number} page
 * @param {number} [pageSize=ADMIN_PAGE_LIMIT]
 * @returns {AdminPageMeta}
 */
export function buildPageMeta(total, page, pageSize = ADMIN_PAGE_LIMIT) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const size = Math.max(1, Number(pageSize) || ADMIN_PAGE_LIMIT);
  const pageCount = Math.max(1, Math.ceil(safeTotal / size) || 1);
  const requested = Math.max(1, Math.floor(Number(page) || 1));
  return {
    total: safeTotal,
    page: Math.min(requested, pageCount),
    pageSize: size,
    pageCount
  };
}

/**
 * Compact page number list with ellipsis markers.
 *
 * @param {number} page
 * @param {number} pageCount
 * @returns {(number|string)[]}
 */
export function visiblePageNumbers(page, pageCount) {
  const count = Math.max(1, pageCount);
  const current = Math.min(Math.max(1, page), count);
  if (count <= 7) {
    return Array.from({ length: count }, (_, i) => i + 1);
  }
  /** @type {(number|string)[]} */
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(count - 1, current + 1);
  if (start > 2) items.push('…');
  for (let n = start; n <= end; n++) items.push(n);
  if (end < count - 1) items.push('…');
  items.push(count);
  return items;
}

/**
 * Renders pagination controls. Always includes Prev/Next and at least page 1.
 *
 * @param {AdminPageMeta} meta
 * @returns {string}
 */
export function renderPaginationHtml(meta) {
  const { page, pageCount, total, pageSize } = buildPageMeta(meta.total, meta.page, meta.pageSize);
  const prevDisabled = page <= 1;
  const nextDisabled = page >= pageCount;
  const numbers = visiblePageNumbers(page, pageCount).map((n) => {
    if (n === '…') return '<span class="page-ellipsis" aria-hidden="true">…</span>';
    const current = n === page;
    return (
      '<button type="button" class="page-num' + (current ? ' active' : '') + '" data-page="' + n + '"' +
      (current ? ' aria-current="page"' : '') +
      '>' + n + '</button>'
    );
  }).join('');

  return (
    '<div class="pagination-info">Page ' + page + ' of ' + pageCount +
    ' · ' + total + ' total · ' + pageSize + ' per page</div>' +
    '<div class="pagination-controls">' +
      '<button type="button" class="page-nav" data-page="' + (page - 1) + '"' +
        (prevDisabled ? ' disabled' : '') + '>Prev</button>' +
      numbers +
      '<button type="button" class="page-nav" data-page="' + (page + 1) + '"' +
        (nextDisabled ? ' disabled' : '') + '>Next</button>' +
    '</div>'
  );
}
